import { PageHeader, DataTable, FilterBar, Empty, Loading } from '../../components/office';
import { useEffect, useMemo, useState } from 'react';
import { suppliersApi, ingredientsApi, stockApi } from '@verlo/shared/services/api';
import SupplierProducts from '../../components/stock/SupplierProducts';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import { TimeField } from '@verlo/shared/components/ui/time-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { phoneProblem, tidyPhone } from '@verlo/shared/lib/phone';

// Suppliers is what the routes can answer: the list, create, update, delete — and which
// ingredients name each supplier (`ingredients.supplier_id`). A supplier's products are the
// stock items that name them, edited in its dialog with its bundles (Back office sweep, Oct 2026). The order history, ratings
// and average-order figures the old page showed came from routes that were never mounted
// and fields the API sets to zero; they are gone rather than shown as noughts.

const EMPTY = { name: '', category: '', contact_name: '', email: '', phone: '', address: '', payment_terms: 'Net 30', notes: '',
  order_cc: '', account_number: '', delivery_days: [], order_by: '', order_days_before: 1, minimum_order: '' };
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// "Tue, Fri"; "Every day"; "" when none set.
const daysWords = (d) => (!d?.length ? '' : d.length === 7 ? 'Every day' : d.map((i) => DAYS[i]).join(', '));

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[12.5px] text-ink-soft">{label}</Label>
    {children}
  </div>
);

const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);   // null | 'new' | supplier
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, i, st] = await Promise.all([suppliersApi.getAll(), ingredientsApi.getAll().catch(() => ({ data: [] })), stockApi.getAll().catch(() => ({ data: [] }))]);
      setSuppliers(s.data);
      setIngredients(i.data || []);
      setStock(st.data || []);
    } catch {
      toast.error("Couldn't load suppliers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const suppliedBy = useMemo(() => {
    const m = {};
    for (const ing of ingredients) {
      if (!ing.supplier_id) continue;
      (m[ing.supplier_id] ||= []).push(ing);
    }
    return m;
  }, [ingredients]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return suppliers
      .filter((s) => !q || s.name.toLowerCase().includes(q) || (s.category || '').toLowerCase().includes(q))
      .map((s) => ({ ...s, supplies: suppliedBy[s.id] || [], products: stock.filter((x) => x.supplier_id === s.id) }));
  }, [suppliers, suppliedBy, stock, search]);

  const open = (s) => {
    setEditing(s || 'new');
    setForm(s ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, s[k] ?? EMPTY[k]])) } : EMPTY);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    // One way of writing a number (Chris's review L19).
    const body = { ...form, phone: tidyPhone(form.phone), minimum_order: parseFloat(form.minimum_order) || 0 };
    try {
      if (editing === 'new') {
        await suppliersApi.create(body);
        toast.success(`${form.name} added`);
      } else {
        await suppliersApi.update(editing.id, body);
        toast.success(`${form.name} saved`);
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save supplier");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}? Ingredients that name this supplier keep their cost but lose the link.`)) return;
    try {
      await suppliersApi.delete(s.id);
      toast.success(`${s.name} deleted`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete supplier");
    }
  };

  const columns = [
    { key: 'name', label: 'Supplier', sortable: true, render: (s) => (
      <div className="min-w-0">
        <div className="font-medium text-ink">{s.name}</div>
        {s.category && <div className="text-[12.5px] text-ink-soft">{s.category}</div>}
      </div>
    ) },
    { key: 'contact', label: 'Contact', muted: true, render: (s) => (
      <div className="text-[12.5px] leading-snug">
        {s.contact_name && <div className="text-ink">{s.contact_name}</div>}
        {[s.email, tidyPhone(s.phone)].filter(Boolean).join(' · ') || <span className="text-ink-faint">—</span>}
      </div>
    ) },
    // Where its orders go and when it delivers, at a glance (Oct 2026: sending orders).
    { key: 'ordering', label: 'Ordering', muted: true, render: (s) => (
      <div className="text-[12.5px] leading-snug">
        {s.email ? <div className="text-ink">Emailed</div> : <div className="text-signal-warn">No email for orders</div>}
        {(daysWords(s.delivery_days) || s.order_by) && <div>{[daysWords(s.delivery_days), s.order_by && `order by ${s.order_by}`].filter(Boolean).join(' · ')}</div>}
      </div>
    ) },
    { key: 'payment_terms', label: 'Terms', muted: true, sortable: true },
    { key: 'products', label: 'Products', align: 'right', sortable: true, sortValue: (s) => s.products.length, render: (s) => (
      s.products.length
        ? <span title={s.products.map((i) => i.name).join(', ')} className="font-mono tabular-nums">{s.products.length} {s.products.length === 1 ? 'product' : 'products'}</span>
        : <span className="text-ink-faint">none yet</span>
    ) },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Suppliers"
        purpose="Who you buy from, and what they supply."
        actions={(
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={() => open(null)} data-testid="add-supplier-btn">
            <Plus size={16} className="mr-2" />
            Add supplier
          </Button>
        )}
      />

      {loading ? <Loading shape="table" /> : suppliers.length === 0 ? (
        <Empty word="Suppliers" title="No suppliers yet" testId="suppliers-empty"
          action={<Button variant="outline" onClick={() => open(null)}>Add the first</Button>}>
          Add who you buy from, then name them on each ingredient so cost of sales knows where it came from.
        </Empty>
      ) : (
        <>
          <FilterBar right={<span>{rows.length} of {suppliers.length}</span>}>
            <Input placeholder="Search suppliers…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" data-testid="supplier-search" />
          </FilterBar>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey="id"
            defaultSort={{ key: 'name', dir: 'asc' }}
            onRowClick={open}
            actions={(s) => (
              <>
                <Button variant="ghost" size="sm" className="h-8" onClick={() => open(s)} data-testid={`supplier-edit-${s.id}`}><Pencil size={14} /></Button>
                <Button variant="ghost" size="sm" className="h-8 text-signal-stop hover:text-signal-stop" onClick={() => remove(s)} data-testid={`supplier-delete-${s.id}`}><Trash2 size={14} /></Button>
              </>
            )}
            empty={{ word: 'Suppliers', title: `Nothing matches “${search}”`, action: <Button variant="outline" onClick={() => setSearch('')}>Clear search</Button> }}
            testId="suppliers-table"
          />
        </>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent size={editing && editing !== 'new' ? 'sheet' : 'form'}>
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add supplier' : form.name || 'Supplier'}</DialogTitle>
            <DialogDescription>
              {editing === 'new' ? 'Who they are and how to reach them.' : 'Who they are, what you buy from them, and the bundles you order.'}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name" autoFocus /></Field>
              <Field label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Produce, meat, dry goods…" /></Field>
              <Field label="Contact name"><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></Field>
              <Field label="Payment terms"><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} /></Field>
              <Field label="Email (orders go here)"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="supplier-email" /></Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} onBlur={() => setForm((f) => ({ ...f, phone: tidyPhone(f.phone) }))} inputMode="tel" />
                {phoneProblem(form.phone) && <p className="mt-1 text-[12px] text-signal-warn" data-testid="supplier-phone-problem">{phoneProblem(form.phone)}</p>}
              </Field>
            </div>
            <Field label="Address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>

            {/* How they take orders (Oct 2026: sending orders). Orders are emailed to the address
                above; the rest tells Review and send which delivery an order makes. */}
            <div className="space-y-3 border-t border-hairline pt-4" data-testid="supplier-ordering">
              <p className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">Ordering</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Copy orders to (optional)"><Input value={form.order_cc} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, order_cc: v })); }} placeholder="e.g. your rep" /></Field>
                <Field label="Your account number with them"><Input value={form.account_number} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, account_number: v })); }} className="font-mono" data-testid="supplier-account" /></Field>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12.5px] text-ink-soft">They deliver on</Label>
                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Delivery days">
                  {DAYS.map((d, i) => {
                    const on = form.delivery_days.includes(i);
                    return (
                      <button key={d} type="button" aria-pressed={on} data-testid={`supplier-day-${d}`}
                        onClick={() => setForm((f) => ({ ...f, delivery_days: on ? f.delivery_days.filter((x) => x !== i) : [...f.delivery_days, i].sort() }))}
                        className={`h-9 min-w-11 rounded-md border px-2.5 text-[13px] ${on ? 'border-verlo bg-verlo text-white' : 'border-hairline-strong bg-white text-ink hover:bg-hover-tint'}`}>{d}</button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Field label="Order by">
                  <TimeField value={form.order_by} onChange={(t) => setForm((f) => ({ ...f, order_by: t }))} allowEmpty aria-label="Order by" testId="supplier-order-by" />
                </Field>
                <Field label="Days before delivery">
                  <Select value={String(form.order_days_before ?? 1)} onValueChange={(v) => setForm((f) => ({ ...f, order_days_before: Number(v) }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3].map((n) => <SelectItem key={n} value={String(n)}>{n === 0 ? 'The same day' : n === 1 ? 'The day before' : `${n} days before`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Minimum order (£)"><Input type="number" min="0" step="0.01" value={form.minimum_order} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, minimum_order: v })); }} placeholder="None" className="font-mono" data-testid="supplier-minimum" /></Field>
              </div>
            </div>
            <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            {editing && editing !== 'new' && (
              <div className="border-t border-hairline pt-4">
                <SupplierProducts supplier={editing} stock={stock} onStockChange={(id, body) => setStock((cur) => cur.map((x) => (x.id === id ? { ...x, ...body } : x)))} />
              </div>
            )}
            {editing !== 'new' && (suppliedBy[editing?.id] || []).length > 0 && (
              <div className="rounded-md border border-hairline bg-surface-2 p-3 text-[12.5px] text-ink-soft">
                <div className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">Recipe ingredients that name them</div>
                <div className="mt-1">{suppliedBy[editing.id].map((i) => i.name).join(', ')}</div>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={saving} data-testid="supplier-save">
              {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
              {editing === 'new' ? 'Add supplier' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuppliersPage;
